/**
 * Showcase Store —— 前台共享状态容器（ADR-0003，F02）。
 *
 * Vue 3 `reactive` + `provide/inject` 自建，**不用 Pinia**（前台 PRD
 * 锁定）。保存 Aggregate / Content Version / 加载状态 / 错误 / isPreview。
 *
 * 状态机：
 *   idle → loading → ready（写缓存）
 *   loading → failed（Critical，Aggregate 拿不到）
 *   failed → retry → loading → ready
 *   ready → revalidating → ready（版本一致 / 不一致重拉成功）
 *   ready → revalidating → ready + error（revalidate 失败，**保留旧
 *     aggregate 不静默抹错**，F7 toast 消费 error 字段）
 *
 * **Debug-First（AGENTS §六）**：失败显式落 failed / ready+error；
 * Preview 模式跳过 sessionStorage 读写（即时快照无版本失效语义）。
 *
 * **reactive 陷阱**：`state` 是 reactive 对象，消费方必须 `store.state.x`
 * 访问，**禁止解构**（解构丢响应式）。`ShowcaseStore.state` 类型层标
 * `Readonly`，业务组件无法直接写 state，状态机迁移走 store action。
 */
import { reactive } from 'vue';

import type { AggregateView } from '@/api/types';
import {
  ApiError,
  NetworkError,
  fetchAggregate,
  fetchPreviewAggregate,
  fetchVersion,
} from '@/api/client';
import { readAggregateCache, writeAggregateCache } from '@/api/cache';

export type ShowcaseStatus = 'idle' | 'loading' | 'ready' | 'revalidating' | 'failed';

export interface ShowcaseError {
  readonly message: string;
  readonly errorCode: string;
  readonly traceId: string;
  readonly status: number;
}

export interface ShowcaseState {
  status: ShowcaseStatus;
  aggregate: AggregateView | null;
  contentVersion: string | null;
  error: ShowcaseError | null;
  isPreview: boolean;
  /**
   * 当前预览 token（F10）。预览态由 `bootstrap` 从 `?preview=` 探测写入；
   * 普通态为 `null`。DetailPage / DetailSidebar / DetailMoreLink 据此
   * 构造跨路由 `?preview=` query 透传（F10 基础透传，F15 闭合全量 Material）。
   */
  previewToken: string | null;
}

export interface ShowcaseStore {
  /** 对外只读：业务组件读 `store.state.x` 触发响应式，写需走 action。 */
  readonly state: Readonly<ShowcaseState>;
  /**
   * 启动初始化；并发安全（已在 loading/revalidating 时早返，避免并发触发）。
   * `options.previewToken` 显式覆盖；未传时从 `globalThis.location.search`
   * 探测 `?preview=` query 决定走 preview 还是 public 分支（A1，F02 边界
   * 内的 URL→token 提取，UI 横幅 / 跨路由透传留 F08）。
   */
  bootstrap(options?: { previewToken?: string }): Promise<void>;
  /** Critical 失败后重试。 */
  retry(): Promise<void>;
  /** ready 态后台版本校验；不一致重拉。Preview 态 no-op。 */
  revalidate(): Promise<void>;
}

const NETWORK_ERROR_CODE = 'network.error';
const EMPTY_TRACE_ID = '';
const NETWORK_STATUS = 0;

/**
 * Store 内部上下文：聚合 state 与可变 previewToken，便于把 action 提到
 * module scope 接 ctx 参数（避免 createShowcaseStore 把所有 action 闭包
 * 内联导致工厂超过函数 50 行硬上限，AGENTS §五）。
 */
interface StoreContext {
  readonly state: ShowcaseState;
  /** 可变 token 槽：bootstrap/retry 内更新；module scope action 通过 ctx 读写。 */
  previewToken: string | undefined;
}

/** ApiError → 同形态映射；NetworkError / 未知 → network.error + 暴露错误形态。 */
function toStoreError(error: unknown): ShowcaseError {
  if (error instanceof ApiError) {
    return {
      message: error.message,
      errorCode: error.errorCode,
      traceId: error.traceId,
      status: error.status,
    };
  }
  if (!(error instanceof NetworkError)) {
    // Debug-First：未知形态的 error 显式暴露，便于诊断；不静默吞咽。
    // eslint-disable-next-line no-console
    console.error('[showcase-store] unexpected error shape', error);
  }
  return {
    message: error instanceof Error ? error.message : String(error),
    errorCode: NETWORK_ERROR_CODE,
    traceId: EMPTY_TRACE_ID,
    status: NETWORK_STATUS,
  };
}

function setReady(state: ShowcaseState, aggregate: AggregateView, version: string): void {
  state.status = 'ready';
  state.aggregate = aggregate;
  state.contentVersion = version;
  state.error = null;
}

function setFailed(state: ShowcaseState, error: unknown): void {
  state.status = 'failed';
  state.error = toStoreError(error);
}

/** revalidate 失败：保留旧 aggregate，status 回 ready，error 非空（不抹错）。 */
function setReadyWithError(state: ShowcaseState, error: unknown): void {
  state.status = 'ready';
  state.error = toStoreError(error);
}

/** 落地新 aggregate：非 preview 写缓存 + setReady + 清 error。 */
function applyFreshAggregate(state: ShowcaseState, agg: AggregateView, isPreview: boolean): void {
  if (!isPreview) {
    writeAggregateCache(agg, agg.contentVersion);
  }
  setReady(state, agg, agg.contentVersion);
}

/** 按当前模式选端点拉 aggregate；preview 缺 token 暴露而非静默回退。 */
async function loadAggregate(isPreview: boolean, previewToken: string | undefined): Promise<AggregateView> {
  if (isPreview) {
    if (previewToken === undefined) {
      throw new NetworkError('预览 token 缺失', undefined);
    }
    return fetchPreviewAggregate(previewToken);
  }
  return fetchAggregate();
}

/**
 * 从当前 location 探测 `?preview=<token>` query。无 location（SSR / 测试
 * 未注入）或无 query 返 undefined。token 空串视为缺失（与 B14 一致：
 * 空 token 必然 401）。
 */
function resolvePreviewTokenFromLocation(): string | undefined {
  const loc = globalThis.location;
  if (loc === undefined) {
    return undefined;
  }
  const token = new URLSearchParams(loc.search).get('preview');
  return token !== null && token !== '' ? token : undefined;
}

async function bootstrapAction(
  ctx: StoreContext,
  options?: { previewToken?: string },
): Promise<void> {
  if (ctx.state.status === 'loading' || ctx.state.status === 'revalidating') {
    return;
  }
  const explicitToken = options?.previewToken;
  const token = explicitToken !== undefined ? explicitToken : resolvePreviewTokenFromLocation();
  if (token !== undefined) {
    ctx.previewToken = token;
    ctx.state.isPreview = true;
    ctx.state.previewToken = token;
    await runFreshLoad(ctx, true);
    return;
  }
  ctx.state.isPreview = false;
  ctx.state.previewToken = null;
  await bootstrapPublic(ctx);
}

async function bootstrapPublic(ctx: StoreContext): Promise<void> {
  const cached = readAggregateCache();
  if (cached !== null) {
    setReady(ctx.state, cached.aggregate, cached.contentVersion);
    await revalidateAction(ctx);
    return;
  }
  await runFreshLoad(ctx, false);
}

/** 共享的 loading→fetch→applyFreshAggregate→ready/failed 链路。 */
async function runFreshLoad(ctx: StoreContext, isPreview: boolean): Promise<void> {
  ctx.state.status = 'loading';
  try {
    const agg = await loadAggregate(isPreview, ctx.previewToken);
    applyFreshAggregate(ctx.state, agg, isPreview);
  } catch (error) {
    setFailed(ctx.state, error);
  }
}

async function retryAction(ctx: StoreContext): Promise<void> {
  await runFreshLoad(ctx, ctx.state.isPreview);
}

async function revalidateAction(ctx: StoreContext): Promise<void> {
  if (ctx.state.isPreview) {
    return;
  }
  ctx.state.status = 'revalidating';
  try {
    const version = await fetchVersion();
    if (version.contentVersion === ctx.state.contentVersion) {
      ctx.state.status = 'ready';
      ctx.state.error = null;
      return;
    }
    const agg = await fetchAggregate();
    applyFreshAggregate(ctx.state, agg, false);
  } catch (error) {
    setReadyWithError(ctx.state, error);
  }
}

export function createShowcaseStore(): ShowcaseStore {
  const ctx: StoreContext = {
    state: reactive<ShowcaseState>({
      status: 'idle',
      aggregate: null,
      contentVersion: null,
      error: null,
      isPreview: false,
      previewToken: null,
    }),
    previewToken: undefined,
  };
  return {
    state: ctx.state,
    bootstrap: (options) => bootstrapAction(ctx, options),
    retry: () => retryAction(ctx),
    revalidate: () => revalidateAction(ctx),
  };
}
